/**
 * CrewTable Component
 * Desktop table view for crew list with sortable columns
 * Updated 2026-03-04: Added clickable duty toggle pill matching mobile design
 */

import React, { useState } from 'react';
import { Users, ChevronUp, ChevronDown, ChevronsUpDown, Ship, Anchor, AlertTriangle } from 'lucide-react';
import CrewQuickActions from './CrewQuickActions';
import ComplianceBadge from './ComplianceBadge';
import { COMPLIANCE_COLORS, STATUS_COLORS } from '../utils/constants';
import { getCrewCompliance } from '../utils/crewHelpers';
import { displayName } from '../viewHelpers';

/**
 * Get warning message for crew being toggled ON duty with compliance issues
 * Returns null if no issues, or a warning message string
 */
const getDutyToggleWarning = (member, familiarisationRecords = [], familiarisationValidityMonths = 12) => {
  const warnings = [];
  const today = new Date();

  // Check for expired certificates
  if (member.certificates && member.certificates.length > 0) {
    const expiredCerts = member.certificates.filter(cert => {
      if (!cert.expiry_date) return false;
      const expiryDate = new Date(cert.expiry_date);
      return expiryDate < today;
    }).map(cert => cert.name || cert.type || 'Certificate');

    if (expiredCerts.length > 0) {
      warnings.push(`Expired: ${expiredCerts.slice(0, 3).join(', ')}${expiredCerts.length > 3 ? '...' : ''}`);
    }
  }

  // Check medical expiry
  if (member.medical_expiry) {
    const medicalExpiry = new Date(member.medical_expiry);
    if (medicalExpiry < today) {
      warnings.push('Medical expired');
    }
  }

  // Check familiarisation status
  if (familiarisationRecords.length > 0) {
    const memberFamRecords = familiarisationRecords.filter(r => r.crew_id === member.id);
    if (memberFamRecords.length === 0) {
      warnings.push('No familiarisation');
    } else {
      // Check if most recent familiarisation is expired
      const latestFam = memberFamRecords.sort((a, b) =>
        new Date(b.date || b.completed_date) - new Date(a.date || a.completed_date)
      )[0];
      if (latestFam) {
        const famDate = new Date(latestFam.date || latestFam.completed_date);
        const expiryDate = new Date(famDate);
        expiryDate.setMonth(expiryDate.getMonth() + familiarisationValidityMonths);
        if (expiryDate < today) {
          warnings.push('Familiarisation expired');
        }
      }
    }
  }

  return warnings.length > 0 ? warnings.join(', ') : null;
};

// Sortable header component with three-state visual indicators
const SortableHeader = ({ label, sortKey, sortConfig, onSort }) => {
  const isSorted = sortConfig.key === sortKey;
  const direction = isSorted ? sortConfig.direction : null;

  return (
    <th
      scope="col"
      onClick={() => onSort(sortKey)}
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
    >
      <div className="flex items-center gap-1">
        {label}
        {!isSorted && <ChevronsUpDown className="w-4 h-4 text-gray-400" />}
        {isSorted && direction === 'asc' && <ChevronUp className="w-4 h-4 text-blue-600" />}
        {isSorted && direction === 'desc' && <ChevronDown className="w-4 h-4 text-blue-600" />}
      </div>
    </th>
  );
};

const CrewTable = ({
  filteredCrew,
  selectedCrew,
  showBulkActions,
  hoursData,
  vesselConfig,
  drillsData,
  familiarisationRecords = [],
  familiarisationValidityMonths = 12,
  onSelectAll,
  onSelectCrew,
  onViewCrew,
  onEditCrew,
  onAddCrew,
  onQuickHOR,
  onQuickSignOn,
  onQuickSignOff,
  onToggleDutyStatus,  // NEW: Toggle duty status callback
  isOnDuty,            // NEW: Helper function to check duty status
  searchTerm,
  isSyncing = false,
  sortConfig = { key: null, direction: null },
  onSort = () => {}
}) => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dutyWarning, setDutyWarning] = useState(null); // Track which member has active warning

  // Handle duty toggle with warning check
  const handleDutyToggle = (member) => {
    const wasOnDuty = isOnDuty && isOnDuty(member);

    // Execute the toggle
    if (onToggleDutyStatus) {
      onToggleDutyStatus(member);
    }

    // If toggling TO On Duty, check for warnings
    if (!wasOnDuty) {
      const warning = getDutyToggleWarning(member, familiarisationRecords, familiarisationValidityMonths);
      if (warning) {
        setDutyWarning({ memberId: member.id, message: warning });
        // Auto-clear warning after 5 seconds
        setTimeout(() => setDutyWarning(null), 5000);
      }
    }
  };

  const getComplianceColor = (type) => {
    return COMPLIANCE_COLORS[type] || COMPLIANCE_COLORS.default;
  };

  const getStatusColor = (status) => {
    return STATUS_COLORS[status] || STATUS_COLORS.default;
  };

  const handleToggleDropdown = (memberId, isOpen) => {
    setActiveDropdown(isOpen ? memberId : null);
  };

  return (
    <div className="mt-4 hidden lg:block">
      <div className="shadow ring-1 ring-black ring-opacity-5 rounded-lg overflow-visible" style={{ overflow: 'visible' }}>
        <div className="overflow-x-auto overflow-y-visible" style={{ overflow: 'visible' }}>
          <table className="min-w-full divide-y divide-gray-300" style={{ overflow: 'visible' }}>
            <thead className="bg-gray-50">
              <tr>
                {showBulkActions && (
                  <th scope="col" className="relative px-4 py-3">
                    <input
                      type="checkbox"
                      className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                      checked={selectedCrew.length === filteredCrew.length && filteredCrew.length > 0}
                      onChange={onSelectAll}
                    />
                  </th>
                )}
                <SortableHeader
                  label="Name"
                  sortKey="full_name"
                  sortConfig={sortConfig}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Position"
                  sortKey="position"
                  sortConfig={sortConfig}
                  onSort={onSort}
                />
                <SortableHeader
                  label="Status"
                  sortKey="status"
                  sortConfig={sortConfig}
                  onSort={onSort}
                />
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Certificates
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Compliance
                </th>
                <th scope="col" className="relative px-4 py-3">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredCrew.length === 0 ? (
                <tr>
                  <td colSpan={showBulkActions ? "7" : "6"} className="px-6 py-12 text-center">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-2 text-sm text-gray-500">
                      {isSyncing
                        ? 'Syncing crew data...'
                        : searchTerm
                          ? `No crew members found matching "${searchTerm}"`
                          : 'No crew members added yet'
                      }
                    </p>
                    {!searchTerm && !isSyncing && (
                      <button
                        onClick={onAddCrew}
                        className="mt-3 text-sm text-cyan-600 hover:text-cyan-500"
                      >
                        Add your first crew member
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                filteredCrew.map((member) => {
                  const compliance = getCrewCompliance(member, hoursData, vesselConfig, drillsData, familiarisationRecords, familiarisationValidityMonths);
                  const isSelected = selectedCrew.includes(member.id);
                  return (
                    <tr key={member.id} className="hover:bg-gray-50">
                      {showBulkActions && (
                        <td className="relative w-12 px-6 sm:w-16 sm:px-8">
                          <input
                            type="checkbox"
                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-600"
                            value={member.id}
                            checked={isSelected}
                            onChange={() => onSelectCrew(member.id)}
                          />
                        </td>
                      )}
                      <td
                        className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6 cursor-pointer"
                        onClick={() => onViewCrew(member)}
                      >
                        <div className="font-medium text-gray-900">{displayName(member)}</div>
                      </td>
                      <td 
                        className="whitespace-nowrap px-3 py-4 text-sm text-gray-900 cursor-pointer"
                        onClick={() => onViewCrew(member)}
                      >
                        {member.position}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <div className="flex flex-col gap-1">
                          {/* Clickable duty toggle pill - matches mobile design */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDutyToggle(member);
                            }}
                            className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
                              isOnDuty && isOnDuty(member)
                                ? 'bg-cyan-50 text-cyan-700 ring-cyan-600/20 hover:bg-cyan-100'
                                : 'bg-gray-50 text-gray-600 ring-gray-500/20 hover:bg-gray-100'
                            }`}
                          >
                            {isOnDuty && isOnDuty(member) ? <Ship className="h-3 w-3" /> : <Anchor className="h-3 w-3" />}
                            {isOnDuty && isOnDuty(member) ? 'On Duty' : 'Off Duty'}
                          </button>
                          {/* Warning message for compliance issues */}
                          {dutyWarning && dutyWarning.memberId === member.id && (
                            <div className="flex items-center gap-1 text-xs text-amber-600">
                              <AlertTriangle className="h-3 w-3" />
                              <span className="truncate max-w-[140px]">{dutyWarning.message}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-4 text-sm">
                        <ComplianceBadge certificates={member.certificates || []} />
                      </td>
                      <td
                        className="px-3 py-4 text-sm cursor-pointer"
                        onClick={() => onViewCrew(member)}
                      >
                        {compliance.issues.length === 0 ? (
                          <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getComplianceColor(compliance.type)}`}>
                            {compliance.text}
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {compliance.issues.map((issue, idx) => (
                              <span 
                                key={idx} 
                                className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${getComplianceColor(issue.type)}`}
                              >
                                {issue.text}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6" style={{ overflow: 'visible' }}>
                        <CrewQuickActions
                          member={member}
                          isOpen={activeDropdown === member.id}
                          onToggle={(isOpen) => handleToggleDropdown(member.id, isOpen)}
                          onRecordHours={onQuickHOR}
                          onSignOn={onQuickSignOn}
                          onSignOff={onQuickSignOff}
                          onViewProfile={onViewCrew}
                          onEditDetails={onEditCrew}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrewTable;